import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ManagementClientSubscriptionResponse, ClientSubscriptionControllerService, PartiallyUpdateClientSubscriptionRequest, ClientTagControllerService, ManagementClientTagResponse, ManagementClientTagRequest, ManagementClientResponse } from 'api-client';
import { EmailControllerService, SettingsData } from 'api-notification';
import { SharedService } from '../../../services/shared.service';
import { take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { ManagementCategoryResponse, ManagementTagResponse, ClientCategoryControllerService as ClippingClientCategoryControllerService } from 'api-clipping';
import { ClientCategoryControllerService as ContractNoticesClientCategoryControllerService } from 'api-contract-notices';
import { UtilsService } from '../../../services/utils.service';
import { AuthenticationService } from '../../../services/authentication.service';

interface ManagementCategoryWithTags extends ManagementCategoryResponse {
	tags?: ManagementTagWithAdditionalInfo[];
	isOpened?: boolean;
	name?: string;
	title?: string;
}

interface ManagementTagWithAdditionalInfo extends ManagementTagResponse {
	sentiment?: boolean;
	aiPrompt?: string;
	checked?: boolean;
	index: number;
	realTime?: boolean;
	realTimeEmails?: string[];
	name?: string;
	title?: string;
	clientTagId?: string;
}

interface DialogData {
	subscription: ManagementClientSubscriptionResponse;
	clientId: string;
	clientData?: ManagementClientResponse;
}

@Component({
	selector: 'app-edit-subscription-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatChipsModule,
		MatExpansionModule,
		MatCheckboxModule,
		MatIconModule,
		MatTooltipModule,
		MatDividerModule,
		TranslateModule
	],
	templateUrl: './edit-subscription-dialog.component.html',
	styleUrl: './edit-subscription-dialog.component.scss'
})
export class EditSubscriptionDialogComponent implements OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();
	
	editForm!: FormGroup;
	separatorKeysCodes: number[] = [ENTER, COMMA];
	selectedEmailsTo: string[] = [];
	selectedEmailsCc: string[] = [];

	@ViewChild('emailToInput', { static: false }) emailTo!: ElementRef<HTMLInputElement>;
	@ViewChild('emailCcInput', { static: false }) emailCc!: ElementRef<HTMLInputElement>;

	showTagsSection = false;
	tagCategories: ManagementCategoryWithTags[] = [];
	clientTags: ManagementClientTagResponse[] = [];
	
	private settingsData: SettingsData = {};
	private addedTagNames: string[] = [];
	private removedTagNames: string[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: DialogData,
		private dialogRef: MatDialogRef<EditSubscriptionDialogComponent>,
		private fb: FormBuilder,
		private subscriptionService: ClientSubscriptionControllerService,
		private clientTagService: ClientTagControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private apiClippingClientCategory: ClippingClientCategoryControllerService,
		private apiContractNoticeClientCategory: ContractNoticesClientCategoryControllerService,
		public utils: UtilsService,
		private emailService: EmailControllerService,
		private authService: AuthenticationService
	) {
		this.initializeForm();
		this.loadExistingData();
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	private initializeForm(): void {
		this.editForm = this.fb.group({
			emailTo: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
			emailCc: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
			clientTagsDetails: this.fb.array([])
		});

		const subscriptionType = this.data.subscription.type;
		this.showTagsSection = (subscriptionType?.includes('CONTRACT_NOTICES') || false);
		
		if (this.showTagsSection) {
			this.loadClientTags();
		}
	}

	private loadExistingData(): void {
		if (this.data.subscription.emailTo) {
			this.selectedEmailsTo = [...this.data.subscription.emailTo];
		}
		if (this.data.subscription.emailCc) {
			this.selectedEmailsCc = [...this.data.subscription.emailCc];
		}
	}

	get clientTagsDetails() {
		return this.editForm.controls['clientTagsDetails'] as FormArray;
	}

	manageEmails(controlName: 'emailTo' | 'emailCc', action: 'REMOVE' | 'ADD', data: string | MatChipInputEvent) {
		const emailPattern: RegExp = new RegExp(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '');
		const inputData = typeof data === 'string' ? data : (data as MatChipInputEvent).value;
		let emailList: string[] = [];
		if (inputData) {
			emailList.push.apply(emailList, inputData.replaceAll(' ', '').split(','));
		}
		
		if (action === 'REMOVE') {
			if (controlName === 'emailTo') {
				this.selectedEmailsTo = this.selectedEmailsTo.filter(email => email !== data);
			} else if (controlName === 'emailCc') {
				this.selectedEmailsCc = this.selectedEmailsCc.filter(email => email !== data);
			}
		} else if (action === 'ADD') {
			emailList.forEach(email => {
				if (emailPattern.test(email)) {
					if (controlName === 'emailTo') {
						this.selectedEmailsTo.push(email);
					} else if (controlName === 'emailCc') {
						this.selectedEmailsCc.push(email);
					}
				}
			});
			this.editForm.controls[controlName].setValue(null);
			if (controlName === 'emailTo' && this.emailTo) {
				this.emailTo.nativeElement.value = '';
			} else if (controlName === 'emailCc' && this.emailCc) {
				this.emailCc.nativeElement.value = '';
			}
		}
	}

	private getActiveCategoryController() {
		return this.data.subscription.type === 'CLIPPING' ? 
			(this.apiClippingClientCategory as any) : 
			(this.apiContractNoticeClientCategory as any);
	}

	private loadClientTags(): void {
		this.clientTagService
			.getClientTagsDetails(this.data.clientId)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.clientTags = response;
					this.loadCategories();
				},
				error: () => {
					this.loadCategories();
				}
			});
	}

	private loadCategories(): void {
		this.getActiveCategoryController()
			.getCategoriesWithTagsForClient()
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					this.tagCategories = response || [];
					this.tagCategories.pop(); // remove "Ostanati tagovi"
					(this.tagCategories as ManagementCategoryWithTags[]).forEach(categoryItem => {
						categoryItem.tags?.forEach(tagItem => {
							tagItem.index = this.clientTagsDetails.length;
							const clientTag = this.clientTags.find(item => item.tagId === tagItem.id);
							this.addClientTagDetailsControl(tagItem.id, clientTag);

							if (clientTag) {
								categoryItem.isOpened = true;
							}
						});
					});
				},
				error: () => {
					this.tagCategories = [];
				}
			});
	}

	private addClientTagDetailsControl(tagId: string, data?: ManagementClientTagResponse) {
		this.clientTagsDetails.push(
			this.fb.group({
				id: [tagId],
				tagType: ['CONTRACT_NOTICES'],
				clientId: [data?.clientId],
				clientTagId: [data?.id],
				aiPrompt: [data?.aiPrompt],
				checked: [data ? true : false],
				index: this.clientTagsDetails.length,
				realTime: [data?.realTime],
				realTimeEmails: [data?.realTimeEmails],
			})
		);
	}

	manageClientTag(index: number, _controlName?: string, eventCheckbox?: MatCheckboxChange) {
		const formGroup = this.clientTagsDetails.controls[index] as FormGroup;

		if (eventCheckbox && _controlName) {
			formGroup.controls[_controlName].setValue(eventCheckbox.checked);
		}

		if (formGroup.controls['checked'].value) {
			if (!formGroup.controls['clientTagId'].value) {
				this.createClientTag(formGroup.value);
			}
		} else {
			this.deleteClientTag(formGroup.value);
		}
	}

	private createClientTag(tag: ManagementTagWithAdditionalInfo) {
		const tagDto: ManagementClientTagRequest = {
			tagId: tag.id,
			tagType: 'CONTRACT_NOTICES',
			priority: 5,
			sentiment: tag.sentiment || false,
			realTime: tag.realTime || false,
			// Viber posting isn't offered from this dialog (CONTRACT_NOTICES tags) - always
			// off here, same as this dialog never having offered a sentiment/real-time UI
			// either beyond what ManagementTagWithAdditionalInfo already carries.
			viber: false,
		};
		
		this.clientTagService
			.createClientTag(tagDto, this.data.clientId)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.updateClientTagsControls(tag.index, response);
					this.clientTags.push(response);
					
					// Track added tag for settings notification
					const tagName = this.getTagNameById(tag.id);
					if (tagName && !this.addedTagNames.includes(tagName)) {
						this.addedTagNames.push(tagName);
						this.loadClientDataForSettings();
					}
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}


	private deleteClientTag(tag: ManagementTagWithAdditionalInfo) {
		this.clientTagService
			.deleteClientTag(this.data.clientId, tag.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.clientTags = this.clientTags.filter(tagItem => tagItem.id !== tag.id);
					this.updateClientTagsControls(tag.index);
					
					// Track removed tag for settings notification
					const tagName = this.getTagNameById(tag.id);
					if (tagName && !this.removedTagNames.includes(tagName)) {
						this.removedTagNames.push(tagName);
						this.loadClientDataForSettings();
					}
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	private updateClientTagsControls(index: number, clientTag?: ManagementClientTagResponse) {
		const formGroup = this.clientTagsDetails.controls[index] as FormGroup;
		formGroup.controls['clientTagId'].setValue(clientTag?.id);
		formGroup.controls['clientId'].setValue(clientTag?.clientId);
	}

	accordionAction(action: 'open' | 'close', category?: ManagementCategoryWithTags) {
		if (category) {
			category.isOpened = action === 'open';
		}
	}

	checkFormValidity(): boolean {
		return this.selectedEmailsTo.length > 0 && this.editForm.valid;
	}

	save(): void {
		if (this.checkFormValidity()) {
			const updateRequest: PartiallyUpdateClientSubscriptionRequest = {
				emailTo: this.selectedEmailsTo,
				emailCc: this.selectedEmailsCc,
				emailBcc: this.data.subscription.emailBcc,
			};

			this.subscriptionService
				.updateClientSubscription(updateRequest, this.data.subscription.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						// Send settings notification email if tags were changed
						this.sendSettingsNotificationIfNeeded();
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS'
						});
						this.dialogRef.close(true);
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR'
						});
					}
				});
		}
	}

	cancel(): void {
		this.dialogRef.close();
	}

	private getTagNameById(tagId: string): string | undefined {
		for (const category of this.tagCategories) {
			const tag = category.tags?.find(t => t.id === tagId);
			if (tag) {
				return this.utils.getParameterByLanguageScript(tag, 'name', true) || this.utils.getParameterByLanguageScript(tag, 'value', true);
			}
		}
		return undefined;
	}

	private loadClientDataForSettings(): void {
		if (this.data.clientData) {
			this.settingsData.clientName = this.data.clientData.name || '';
		}
		const loginData = this.authService.loginData.getValue();
		if (loginData?.identifier) {
			this.settingsData.clientEmail = loginData.identifier;
		}
		if (this.data.subscription.type) {
			this.settingsData.clientSubscription = this.data.subscription.type;
		}
	}

	private sendSettingsNotificationIfNeeded(): void {
		// Only send notification if there were tag changes
		if (this.addedTagNames.length === 0 && this.removedTagNames.length === 0) {
			return;
		}

		this.settingsData.addedTags = this.addedTagNames.join(', ');
		this.settingsData.removedTags = this.removedTagNames.join(', ');

		// The endpoint requires the `client:subscriptions` scope; mainInterceptor attaches the
		// bearer token for all notification-service URLs, so nothing extra is needed here.
		this.emailService.sendClientSettingsInfoToReseller(this.settingsData).pipe(take(1)).subscribe({
			error: err => {
				console.error('Failed to send settings-info notification', err);
			},
		});
	}
}